/**
 * PPSR discharge, fired by the money rather than by somebody remembering.
 *
 * Winnie, 18 September: "PPSR discharge should be fully automatic — triggered the moment
 * payment_status reaches Fully Paid, and only relevant for vehicles Direct Auto actually
 * lodged a PPSR registration against (not every car has one; those without one are just
 * automatically clear)... there's no manual step, and therefore no screen element blocking on
 * a person remembering to click it."
 *
 * Three things this file is careful about, all of them because the discharge is a real call
 * to the national register and real calls fail:
 *
 * 1. A failure is recorded as a failure. `ppsr_discharged` stays false, the error the
 *    register gave is kept, and the vehicle does not close out. Winnie: "A failure must not be
 *    swallowed as a success."
 * 2. Nothing retries on its own. A genuinely broken lodgement — the wrong VIN registered in
 *    the first place — would otherwise hammer the register forever with nobody noticing. The
 *    retry is a button on the PPSR Register report.
 * 3. A vehicle with no lodgement is clear immediately. Most acquisitions never lodge, and
 *    making them wait on an API call that has nothing to discharge would gate the majority of
 *    settlements on a request that can only fail.
 *
 * The call itself goes through `/api/ppsr/discharge`, which has existed since the PPSR Cloud
 * integration and holds the client secret server-side.
 */
import { supabase } from '../lib/supabase'
import { ppsrService } from './ppsrService'

export type DischargeStatus = 'NotApplicable' | 'Pending' | 'Discharged' | 'Failed'

export interface Lodgement {
  id: string
  dealId: string
  vin: string
  registrationNumber: string | null
  status: string
  lodgedAt: string | null
  dischargeStatus: DischargeStatus
  dischargeAttemptedAt: string | null
  dischargedAt: string | null
  dischargeReference: string | null
  dischargeError: string | null
}

const toLodgement = (r: Record<string, unknown>): Lodgement => ({
  id: String(r.id),
  dealId: String(r.deal_id),
  vin: String(r.vin || ''),
  registrationNumber: (r.registration_number as string) || null,
  status: String(r.status || ''),
  lodgedAt: (r.submitted_at as string) || null,
  dischargeStatus: (r.discharge_status as DischargeStatus) || 'Pending',
  dischargeAttemptedAt: (r.discharge_attempted_at as string) || null,
  dischargedAt: (r.discharged_at as string) || null,
  dischargeReference: (r.discharge_reference as string) || null,
  dischargeError: (r.discharge_error as string) || null,
})

/**
 * The live lodgement for a deal, or null.
 *
 * "0 or 1 per Vehicle." Where history has left more than one row, the most recent is the live
 * one — ordered rather than `.maybeSingle()`, which would throw on exactly the data this has
 * to cope with.
 */
export async function loadLodgement(dealId: string): Promise<Lodgement | null> {
  const { data, error } = await supabase
    .from('ppsr_registrations')
    .select('*')
    .eq('deal_id', dealId)
    .order('submitted_at', { ascending: false })
    .limit(1)
  if (error) throw error
  const row = (data || [])[0] as Record<string, unknown> | undefined
  return row ? toLodgement(row) : null
}

export interface DischargeOutcome {
  /** Did the vehicle end up clear? True also when there was nothing to discharge. */
  discharged: boolean
  status: DischargeStatus
  message: string
  /** False when this call did not attempt anything — already done, or nothing to do. */
  attempted: boolean
}

/**
 * Run the discharge for a deal whose money is now all in.
 *
 * Safe to call more than once: an already-discharged registration is reported as done and the
 * register is not called again. A payment corrected downwards and then back up would
 * otherwise re-discharge something that is already gone.
 */
export async function dischargeOnFullyPaid(dealId: string): Promise<DischargeOutcome> {
  let lodgement: Lodgement | null = null
  try {
    lodgement = await loadLodgement(dealId)
  } catch (e) {
    /* Not being able to read our own records is not the same as a failed discharge, and it
       must not mark the vehicle clear. */
    return {
      discharged: false,
      status: 'Failed',
      attempted: false,
      message: e instanceof Error ? e.message : 'The PPSR record could not be read.',
    }
  }

  if (!lodgement) {
    await markDealDischarged(dealId, true)
    return {
      discharged: true,
      status: 'NotApplicable',
      attempted: false,
      message: 'No PPSR registration was lodged against this vehicle, so there is nothing to discharge.',
    }
  }

  if (lodgement.dischargeStatus === 'Discharged' || lodgement.dischargedAt) {
    await markDealDischarged(dealId, true)
    return {
      discharged: true,
      status: 'Discharged',
      attempted: false,
      message: 'Already discharged.',
    }
  }

  return attemptDischarge(dealId, lodgement)
}

/**
 * The manual retry from the PPSR Register report. Same call, same recording — the only
 * difference is that a person asked for it.
 */
export async function retryDischarge(dealId: string): Promise<DischargeOutcome> {
  const lodgement = await loadLodgement(dealId)
  if (!lodgement) {
    await markDealDischarged(dealId, true)
    return {
      discharged: true,
      status: 'NotApplicable',
      attempted: false,
      message: 'There is no lodgement against this vehicle to discharge.',
    }
  }
  return attemptDischarge(dealId, lodgement)
}

async function attemptDischarge(dealId: string, lodgement: Lodgement): Promise<DischargeOutcome> {
  const attemptedAt = new Date().toISOString()
  await patchLodgement(lodgement.id, { discharge_attempted_at: attemptedAt })

  const res = await ppsrService.generateDischarge(dealId, lodgement.vin, '', '')

  if (res.success) {
    const reference = res.details?.ppsr_reference || lodgement.registrationNumber || null
    await patchLodgement(lodgement.id, {
      discharge_status: 'Discharged',
      discharge_reference: reference,
      discharge_error: null,
      /* discharged_at and status are written server-side by the endpoint itself, which holds
         the service role. Writing them again from here would be a second source of truth for
         the same fact. */
    })
    await markDealDischarged(dealId, true)
    return { discharged: true, status: 'Discharged', attempted: true, message: res.message }
  }

  /* The register said no. Recorded as Failed, with its words kept, and the vehicle stays
     un-discharged so it cannot close out behind the gate. */
  await patchLodgement(lodgement.id, {
    discharge_status: 'Failed',
    discharge_error: res.message || 'The discharge was refused and no reason was given.',
  })
  await markDealDischarged(dealId, false)
  return {
    discharged: false,
    status: 'Failed',
    attempted: true,
    message: res.message || 'The PPSR discharge failed.',
  }
}

async function patchLodgement(id: string, patch: Record<string, unknown>): Promise<void> {
  const { error } = await supabase.from('ppsr_registrations').update(patch).eq('id', id)
  if (error) console.error('PPSR lodgement could not be updated', error)
}

async function markDealDischarged(dealId: string, discharged: boolean): Promise<void> {
  const { error } = await supabase.from('deals').update({ ppsr_discharged: discharged }).eq('id', dealId)
  if (error) console.error('Could not record the discharge outcome on the deal', error)
}
